<template>
	<el-card>
		<div slot="header" class="questionnaire header">
			<img src="../image/assessment.png" class="header-icon">
			<span class="header-title">Assessment / Plan</span>
		</div>
		<template v-if="!Object.keys(allAssessments).length">
			<div class="no-assessment-container">
				<img src="../image/no-assessment.png">
				<span class="no-assessment-header">Not enough data available to generate results yet</span>
			</div>
		</template>
		<template v-else>
			<!-- Dropdown -->
			<el-row class="appointment-select-container">
				<el-col :span="12">
					<el-select @change="changeAppointment" v-model="selectedAppointment" class="appointment-dropdown" placeholder="Select Appointment">
						<el-option
							v-for="item in appointments"
							:key="item.value"
							:label="item.label"
							:value="item.value">
						</el-option>
					</el-select>
				</el-col>
				<el-col :span="12" class="button-section">
					<button class="emr-button" v-if="isIntegrationPatient"
							:disabled="!isAssessmentSelected || selectedAssessmentNone" 
							@click="pushEncounterNotes()">
							Push to EMR Encounter Note
					</button>
					<button class="copy-button" 
							:disabled="!isAssessmentSelected || selectedAssessmentNone" 
							@click="copyNotes()">
							Copy
					</button>
				</el-col>
			</el-row>
			<!-- Assessment section -->
			<el-row v-if="parsedAssessments.length" class="assessment-options-container">
				<el-col :span="12">
					<span class="assessment-header">
						Select Diagnosis / ASSESSMENT
						<el-tooltip placement="right" effect="dark" :open-delay="1000">
							<img src="../image/assessment_info.png">
							<div slot="content">
								{{ 'Possible diagnosis based on the patient\'s response to' }}
								<span :key="questionnaire" v-for="questionnaire in appointmentQuestionnaires[selectedAppointment]">
									<br>&nbsp;&nbsp;&nbsp;&bull;&nbsp;&nbsp;
									{{ questionnaire }}
								</span>
							</div>
						</el-tooltip>
					</span>
					<span class="assessment-subheader">Below are the potential diagnosis for this patient based on their responses to the questionnaires. 
						This is for informational purposes only and should always be verified by the physician. 
						Please select one of the below to see the generated note.
					</span>
					<div v-for="(option, index) in parsedAssessments[getSelectedAppointmentIndex].assessments" :class="['assessment-option', selectedAssessment[index] ? 'selected-assessment' : '']" :key="option.id">
						<el-checkbox @change="changeSelectedAssessment(index)" v-model="selectedAssessment[index]">
							<span class="checkbox-label"><span class="option-text">{{ option.name }}</span> {{ option.name !== 'None of the Above' ? '(ICD-10 Code: ' + option.id + ')' : '' }}</span>
						</el-checkbox>
					</div>
					<div v-if="selectedAssessmentNone" class="selected-assessment-none">Thank you for your feedback. We are actively improving our results.</div>
				</el-col>
				<el-col :span="12" :style="selectedAssessmentNone ? 'opacity: 0.7' : '' ">
					<el-card :class="['selected-assessment-card', selectedAssessmentNone ? 'card-disabled' : '']">
						<span class="assessment-chart-header">Assessment: </span>
						<span v-if="!isAssessmentSelected || selectedAssessmentNone" class="default-chart-subheader">Notes will be generated here.</span>
						<span v-else class="assessment-chart-notes">
							<span v-for="(option, index) in parsedAssessments[getSelectedAppointmentIndex].assessments" :key="option.id">
								<span v-if="selectedAssessment[index]">
									Diagnosis: <b>{{ selectedAssessmentNone ? '' : option.name }}</b>
									<br>
									ICD-10 Code: <b>{{ selectedAssessmentNone ? '' : option.id }}</b>
									<br><br>
									<textarea class="textarea-input" :disabled="selectedAssessmentNone" :value="option.details" @input="updateAssessmentNotes($event, index)"></textarea>
								</span>
							</span>
						</span>
					</el-card>
				</el-col>
			</el-row>
			<!-- Treatment section -->
			<el-row class="treatment-options-container" :style="selectedAssessmentNone ? 'opacity: 0.7' : '' ">
				<el-col :span="12">
					<span class="treatment-header">Select Treatment Plans</span>
					<span v-if="!isAssessmentSelected || selectedAssessmentNone" class="treatment-subheader">Possible treatment plans will be generated once Diagnosis is selected.</span>
					<template v-else-if="isAssessmentSelected && parsedTreatments.length">
						<span class="treatment-subheader">Thanks! For a diagnosis of {{ getSelectedAssessment.name }}, the system has identified the possible treatment options below. Please select one to see the generated note.</span>
						<div v-for="(treatment, index) in parsedTreatments[0].treatments" :class="['treatment-option', selectedTreatment[index] ? 'selected-treatment' : '']" :key="treatment.id">
							<el-checkbox @change="changeSelectedTreatment(index)" v-model="selectedTreatment[index]">
								<span class="checkbox-label"><span class="option-text">{{ treatment.name }}</span> {{ treatment.name !== 'None of the Above' ? '(CPT Code: ' + treatment.id + ')' : '' }}</span>
							</el-checkbox>
						</div>
						<div v-if="selectedTreatmentNone" class="selected-treatment-none">Thank you for your feedback. We are actively improving our results.</div>
					</template>
				</el-col>
				<el-col :span="12" :style="selectedTreatmentNone ? 'opacity: 0.7' : '' ">
					<el-card :class="['selected-assessment-card', selectedAssessmentNone || selectedTreatmentNone ? 'card-disabled' : '']">
						<span class="assessment-chart-header">Plan: </span>
						<span v-if="!isTreatmentSelected || selectedTreatmentNone" class="default-chart-subheader">Notes will be generated here.</span>
						<span v-else class="assessment-chart-notes">
							<span v-for="(option, index) in parsedTreatments[0].treatments" :key="option.id">
								<span v-if="selectedTreatment[index]">
									Treatment Plan: <b>{{ selectedTreatmentNone ? '' : option.name }}</b>
									<br>
									CPT Code: <b>{{ selectedTreatmentNone ? '' : option.id }}</b>
									<br><br>
									<textarea class="textarea-input" :disabled="selectedTreatmentNone" :value="option.details" @input="updateTreatmentNotes($event, index)"></textarea>
								</span>
							</span>
						</span>
					</el-card>
				</el-col>
			</el-row>
		</template>
	</el-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import formatDate from '@/module/util/format-date'

import OverviewCard from '..'
// import LoadingButton from '@/module/component/LoadingButton'

import QuestionnaireBucketMixin from '../mixin/questionnaire-bucket'

export default {
	name: 'patient-assessment-card',
	extends: OverviewCard,

	mixins: [QuestionnaireBucketMixin],

	data() {
		return {
			appointments: [],
			selectedAssessmentNone: false,
			selectedTreatmentNone: false,
			selectedAppointment: '',
			allAssessments: [],
			parsedAssessments: [],
			selectedAssessment: [],
			parsedTreatments: [],
			selectedTreatment: [],
			appointmentQuestionnaires: {}
		}
	},

	mounted() {
		this.allAssessments = this.getPatientAssessment(this.patient.id)
		if (this.allAssessments) {
			this.parseAssessmentAppointments()
			this.parseAppointmentQuestionnaires()
			this.parseAssessments()
		}
	},

	methods: {
		...mapActions('provider', [
			'pushNoteCategory',
			'updateEMREncounterNotes'
		]),
		formatDate,
		changeAppointment() {
			// Initializing the selectedAssessment Array with false
			if (this.parsedAssessments.length) 
				this.selectedAssessment = new Array(this.parsedAssessments[this.getSelectedAppointmentIndex].assessments.length).fill(false)
			this.selectedTreatment = []
			this.selectedAssessmentNone = false
			this.selectedTreatmentNone = false
			this.loadLastSelection()
		},
		updateAssessmentNotes(event, index) {
			this.parsedAssessments[this.getSelectedAppointmentIndex].assessments[index].details = event.target.value
			let selectedId = this.parsedAssessments[this.getSelectedAppointmentIndex].assessments[index].id
			// update allAssessments with the new notes
			Object.keys(this.allAssessments).forEach((assessmentId) => {
				let codeObj = this.allAssessments[assessmentId]
				codeObj['icd-10-details'][selectedId] = event.target.value
				this.allAssessments[assessmentId] = codeObj
			})
		},
		updateTreatmentNotes(event, index) {
			this.parsedTreatments[0].treatments[index].details = event.target.value
			let selectedId = this.parsedTreatments[0].treatments[index].id
			// update allAssessments with the new treatment notes
			Object.keys(this.allAssessments).forEach((assessmentId) => {
				let codeObj = this.allAssessments[assessmentId]
				codeObj['cpt-details'][selectedId] = event.target.value
				this.allAssessments[assessmentId] = codeObj
			})
		},
		copyNotes() {
			let finalCopyText = this.getCombinedNotes
			navigator.clipboard.writeText(finalCopyText)
			this.$message({
                message: 'Notes copied to clipboard',
                type: 'success'
            })
		},
		pushEncounterNotes() {
			this.saveEncounterNotes()
			// Push to emr API
			let scheduleId = Object.keys(this.allAssessments)[this.getSelectedAppointmentIndex]
			if (scheduleId.length) scheduleId = scheduleId.split(':')[1]
			const queryData = {
                schedule: scheduleId,
                id: this.patient.id,
                provider: this.patientProviderProfileList[0].provider,
                emrs: [{
                    emr_category: 'encounter_note',
                    combined_notes: this.getCombinedNotes
                }]
            }
            this.pushNoteCategory(queryData)
                .then((response) => {
					this.$message({
                            message: response.success ? 'Pushed to Encounter notes' : 'Failed to push to Encounter notes',
                            type: response.success ? 'success' : 'error'
                        })
                }).catch((error) => {
                    if (!error.success)
                        this.$message.error(error.description)
                }).finally((_) => {})
		},
		saveEncounterNotes() {
			let id = Object.keys(this.allAssessments)[this.getSelectedAppointmentIndex]
			let updateObj = this.allAssessments[id]

			updateObj['selection'] = {
				'icd-10': this.getSelectedAssessment.id,
				'cpt': this.getSelectedTreatment ? this.getSelectedTreatment.id : ''
			}
			this.updateEMREncounterNotes({
				id,
				patient: this.patient.id,
				provider: this.patientProviderProfileList[0].provider,
				updateObj
			}).then(() => {
				// this.$message({
				// 	message: 'Pushed Encounter Notes to EMR successfully',
				// 	type: 'success'
				// })
			}).catch((err) => {
				this.$message({
					message: 'Couldn\'t save: ' + err,
					type: 'error'
				})
			})
		},
		changeSelectedTreatment(index) {
			this.selectedTreatment.forEach((_, i) => {
				if (i !== index) this.selectedTreatment[i] = false
			})

			// Check if none of the treatment is selected
			let curSelectedTreatment = this.getSelectedTreatment
			if (curSelectedTreatment && curSelectedTreatment.id === 'NOTA') 
				this.selectedTreatmentNone = true
			else 
				this.selectedTreatmentNone = false
			this.saveEncounterNotes()
		}, 
		changeSelectedAssessment(index) {
			this.selectedAssessment.forEach((_, i) => {
				if (i !== index) this.selectedAssessment[i] = false
			})
			// Reset data related to Treatment (if any)
			this.parsedTreatments = []
			this.selectedTreatmentNone = false
			this.selectedTreatment = []

			let curSelectedAssessment = this.getSelectedAssessment
			if (curSelectedAssessment && curSelectedAssessment.id === 'NOTA') {
				this.selectedAssessmentNone = true
			} else this.selectedAssessmentNone = false

			if (this.selectedAssessment[index] && !this.selectedAssessmentNone) this.loadTreatment()

			this.saveEncounterNotes()
		}, 
		loadTreatment() {
			this.parsedTreatments = []
			Object.keys(this.allAssessments).filter(assessmentId => assessmentId.includes(this.selectedAppointment))
											.forEach((assessmentId) => {
												let reqObj = {
													id: assessmentId,
													treatments:[]
												}
												let icd10Cpt = this.allAssessments[assessmentId]['icd-10-cpt']
												let icd = this.getSelectedAssessment.id
												icd10Cpt[icd].forEach(option => {
													reqObj.treatments.push({
														id: option,
														name: this.allAssessments[assessmentId]['cpt-name'][option],
														details: this.allAssessments[assessmentId]['cpt-details'][option]
													})
												})	
												reqObj.treatments.push({ id: 'NOTA', name: 'None of the Above' })
												this.parsedTreatments.push(reqObj)
											})
			// Initializing the selectedTreatment Array with false
			if (this.parsedTreatments.length) this.selectedTreatment = new Array(this.parsedTreatments[0].treatments.length).fill(false)

		},
		parseAppointmentQuestionnaires() {
			this.appointmentQuestionnaires = {}
			Object.keys(this.allAssessments).forEach((assessmentId, index) => {
				let scheduleId = assessmentId.split(':')[1]
				this.appointmentQuestionnaires[scheduleId] = []
				this.patientEvents.filter(event => event.schedule_ref === scheduleId && event.hasOwnProperty('type') && event.type.includes('questionnaire'))
								 .forEach(event => {
									let qId = event.type.split(':')[1]
									let qName = this.getContentKey('questionnaire', qId, 'display_name') || this.getContentKey('questionnaire', qId, 'title')
									this.appointmentQuestionnaires[scheduleId].push(qName)
								})
			})
		},
		parseAssessmentAppointments() {
			if (Object.keys(this.allAssessments).length === 0) return
			this.appointments = []
			Object.keys(this.allAssessments).forEach((assessmentId, index) => {
				let scheduleId = assessmentId.split(':')[1]
				// Filter events on the basis of event type, schedule_ref and time key
				// if there are multiple events then pick only the latest one using time key
				this.orderedSchedule.filter(schedule => schedule.id === scheduleId)
									.slice(0, 1)
									.map(event => {
										this.appointments.push({ time: event.time , value: scheduleId, label: 'ASSESSMENT - ' + this.getEventName(event.event) + ', ' + this.formatDate(event.time) })
									})
			})
			// sort appointments based on time, latest first
			this.appointments.sort((a, b) => b.time - a.time)
			// Select the first appointment as default
			if (this.appointments.length) this.selectedAppointment = this.appointments[0].value
		},
		parseAssessments() {
			Object.keys(this.allAssessments).forEach((assessmentId, index) => {
				let reqObj = {
					id: assessmentId,
					assessments:[]
				}
				let icd10Cpt = this.allAssessments[assessmentId]['icd-10-cpt']
				Object.keys(icd10Cpt).forEach(icd => {
					let reqAssessmentObj = {
						id: icd,
						name: this.allAssessments[assessmentId]['icd-10-name'][icd],
						details: this.allAssessments[assessmentId]['icd-10-details'][icd]
					}
					reqObj.assessments.push(reqAssessmentObj)
				})
				// Adds 'none' option
				reqObj.assessments.push({ id: 'NOTA', name: 'None of the Above' })
				this.parsedAssessments.push(reqObj)
			})

			// Initializing the selectedAssessment Array with false
			if (this.parsedAssessments.length) 
				this.selectedAssessment = new Array(this.parsedAssessments[this.getSelectedAppointmentIndex].assessments.length).fill(false)

			this.loadLastSelection()
		},
		loadLastSelection() {
			if (Object.keys(this.allAssessments).length === 0) return
			// Load last selection using the 'selection' key in allAssessments
			let id = Object.keys(this.allAssessments)[this.getSelectedAppointmentIndex]
			let lastSelection = this.allAssessments[id].selection
			if (lastSelection) {
				let icd10 = lastSelection['icd-10'] ? lastSelection['icd-10'] : ''
				let cpt = lastSelection['cpt'] ? lastSelection['cpt'] : ''
				this.selectedAssessment.forEach((_, index) => {
					if (this.parsedAssessments[this.getSelectedAppointmentIndex].assessments[index].id === icd10) {
						if (icd10 === 'NOTA') 
							this.selectedAssessmentNone = true
						this.selectedAssessment[index] = true
						this.loadTreatment()
					}
				})
				this.selectedTreatment.forEach((_, index) => {
					if (this.parsedTreatments[0].treatments[index].id === cpt) {
						if (cpt === 'NOTA') 
							this.selectedTreatmentNone = true
						this.selectedTreatment[index] = true
					}
				})
			}
		}
	},

	computed: {
		...mapGetters("provider", [
			"getPatient",
			"getPatientAssessment",
			"getPatientSchedule",
			"getPatientProviderProfileList"
		]),
		...mapGetters('content', [ 'getContent', 'getContentKey' ]),
		schedule() {
			return this.getPatientSchedule(this.patient.id)
		},
		orderedSchedule() {
			if (this.schedule)
				// sort the schedule based on time, latest first
				return this.schedule.slice().sort((a, b) => b.time - a.time)
			return []
		},
		getEventName() {
			return (id) => {
				let event = this.getContent("event", id)
				if (event) return event.name
			}
		},
		getSelectedAppointmentIndex() {
			return this.parsedAssessments.findIndex(assessment => assessment.id.includes(this.selectedAppointment))
		},
		isIntegrationPatient() {
			const integrationKeys = ['athena', 'drchrono', 'ecw', 'carecloud']
			return (integrationKeys.some(key => this.patient.hasOwnProperty(key)) || (this.patient.source_type && this.patient.source_id))
		},
		getCombinedNotes() {
			let assessmentNotes = ''
			let treatmentNotes = ''
			if (this.selectedAssessmentNone) {
				this.$message({
					message: 'No notes to copy',
					type: 'error'
				})
				return ''
			}
			this.parsedAssessments[this.getSelectedAppointmentIndex].assessments.forEach((option, index) => {
				if (this.selectedAssessment[index]) {
					assessmentNotes = 'Diagnosis: ' + option.name + '\n' 
										+ 'ICD-10 Code: ' + option.id + '\n\n' 
										+ option.details
				}
			})

			if (!this.selectedTreatmentNone) {
				this.parsedTreatments[0].treatments.forEach((option, index) => {
					if (this.selectedTreatment[index]) {
						treatmentNotes = 'Plan: ' + option.name + '\n' 
											+ 'CPT Code: ' + option.id + '\n\n' 
											+ option.details
					}
				})
			}

			let finalCopyText = ''
			if (assessmentNotes.length && !treatmentNotes.length)
				finalCopyText = assessmentNotes
			else if (!assessmentNotes.length && treatmentNotes.length)
				finalCopyText = treatmentNotes
			else if (!assessmentNotes.length && !treatmentNotes.length) {
				this.$message({
					message: 'No notes to copy',
					type: 'error'
				})
				return ''
			}
			else {
				finalCopyText = assessmentNotes + '\n\n' + treatmentNotes
			}
			return finalCopyText
		},
		isAssessmentSelected() {
			return this.selectedAssessment.some(assessment => assessment)
		},
		isTreatmentSelected() {
			return this.selectedTreatment.some(treatment => treatment)
		},
		getSelectedAssessment() {
			if (this.parsedAssessments.length) {
				let req = this.parsedAssessments[this.getSelectedAppointmentIndex].assessments.filter((_, index) => this.selectedAssessment[index])
				if (req.length) return req[0]
			}
			return {}
		},
		getSelectedTreatment() {
			if (this.parsedTreatments.length) {
				let req = this.parsedTreatments[0].treatments.filter((_, index) => this.selectedTreatment[index])
				if (req.length) return req[0]
			}
			return {}
		}
	},

	components: {
		// LoadingButton
	}
}
</script>

<style lang="scss" scoped>
	@import "../style.scss";
	.no-assessment-container {
		height: 100px;
		display: flex;
		justify-content: start;
		padding-left: 20px;
		align-items: center;
		img {
			width: 40px;
			height: 40px;
		}
		.no-assessment-header {
			margin: 15px;
			font-size: 14px;
			display: block;
			font-weight: 400;
			line-height: 17px;
			letter-spacing: 0.20000000298023224px;
			text-align: left;
		}
	}
	.el-card .header .send-link {
		float: right;
		padding: 0;
		margin-top: -4px;
		span {
			display: inline-block;
			font-size: 12px;
			padding: 0;
			font-weight: 500;
			background-color: #833e93;
			color: #ffffff;
			height: 26px;
			padding-left: 10px;
			padding-right: 10px;
			cursor: pointer;
		}
	}
	.appointment-dropdown {
		width: 500px;
		margin: 20px;
	}


	.el-input--suffix .el-input__inner {
		border-radius: 7px;
		border: 1px solid #000000;
		font-size: 14px;
		font-weight: 600;
		line-height: 17px;
	}

	.appointment-select-container {
		margin-top: 20px;
		position: relative;
		.button-section {
			position: absolute;
			top: 20px;
			right: 10px;
			.copy-button {
				width: 62px;
				height: 24px;
			}
			button {
				float: right;
				margin-right: 10px;
				width: auto;
				height: 24px;
				background-color: #3572B9;
				color: #ffffff;
				padding: 13px, 16px, 13px, 16px;
				border-radius: 4px;
				border: 0.5px;
			}
			button:disabled {
				background-color:#B3B9BE;
				color: #ffffff;
				cursor: not-allowed;
			}
		}
	}

	.assessment-options-container {
		padding: 20px;
		.assessment-header {
			font-size: 14px;
			font-weight: 600;
			line-height: 17px;
			letter-spacing: 0.20000000298023224px;
			text-align: left;
			text-transform: uppercase;
			img {
				cursor: pointer;
				width: 20px;
				height: 20px;
				margin-bottom: -5px;
				margin-left: 5px;
			}
		}
		.assessment-subheader {
			display: block;
			font-size: 14px;
			font-weight: 400;
			line-height: 19px;
			letter-spacing: 0.11812499910593033px;
			text-align: left;
			margin: 15px 0;
		}

		.assessment-option {
			padding: 10px;
			min-height: 44px;
			border-radius: 4px;
			background-color: #EEEEEE;
			margin-bottom: 10px;
			line-height: 24px;
			.el-checkbox {
				display: inline-flex;
				align-items: center;
			}
		}
		.selected-assessment {
			background: #E2F0FF;
		}
		.selected-assessment-none {
			font-size: 14px;
			font-weight: 700;
			line-height: 18px;
			letter-spacing: 0px;
			text-align: left;
			color: #DA4E4E;
		}
	}

	.checkbox-label {
		letter-spacing: 1px;
		white-space: pre-wrap;
		.option-text {
			font-weight: 700;
		}
	}

	.treatment-options-container {
		padding: 20px;
		.treatment-header {
			font-size: 14px;
			font-weight: 600;
			line-height: 17px;
			letter-spacing: 0.20000000298023224px;
			text-align: left;
			text-transform: uppercase;
		}
		.treatment-subheader {
			display: block;
			font-size: 14px;
			font-weight: 400;
			line-height: 19px;
			letter-spacing: 0.11812499910593033px;
			text-align: left;
			margin: 15px 0;
		}

		.treatment-option {
			padding: 10px;
			height: auto;
			min-height: 44px;
			border-radius: 4px;
			background-color: #EEEEEE;
			margin-bottom: 10px;
			line-height: 24px;
			.el-checkbox {
				display: inline-flex;
				align-items: center;
			}
		}
		.selected-treatment {
			background: #E2F0FF;
		}
		.selected-treatment-none {
			font-size: 14px;
			font-weight: 700;
			line-height: 18px;
			letter-spacing: 0px;
			text-align: left;
			color: #DA4E4E;
		}
	}

	.el-card.is-always-shadow {
		box-shadow: none;
	}

	.card-disabled {
		background-color: #F8F8F8;
	}

	.selected-assessment-card {
		margin-left: 20px;
		min-height: 350px;
		max-height: 400px;
		border: 1px solid #CCCCCC;
		span {
			display: block;
		}
		.assessment-chart-header {
			margin-bottom: 20px;
			font-size: 14px;
			font-weight: 700;
			line-height: 23px;
			letter-spacing: 0px;
			text-align: left;
		}
		.default-chart-subheader {
			color: #666666;
			font-size: 13px;
			font-weight: 500;
			line-height: 23px;
			letter-spacing: 0px;
			text-align: left;
		}
		.assessment-chart-notes {
			font-size: 13px;
			font-weight: 500;
			line-height: 23px;
			letter-spacing: 0px;
			text-align: left;
		}

		.textarea-input {
			border: none;
			height: 210px;
			width: 100% !important;
			resize: none;
			outline: none;
			margin-left: -2px;
			line-height: 23px;
		}
		.textarea-input:disabled {
			background-color: #FFFFFF;
		}
	}
	
	.header-icon {
		height: 43px !important;
		margin-top: 0px !important;
	}
</style>
